/**
 * lightbox — one export, two ways in.
 *
 *   html`<img src=${src} ${lightbox()} />`   the directive: any element
 *                                            becomes a trigger
 *   lightbox.push({ images, index })         the service: open it from
 *                                            code, with no trigger element
 *
 * Both are thin wrappers over a plain `show()`, so the viewer opens the way
 * every other overlay does — the same focus handling, dismissal paths, history
 * and stacking — and neither builds an overlay of its own.
 *
 * What both DO name is the layout, and it is the only thing in the library that
 * may: `viewer` is a member of the overlay's own vocabulary, switched on
 * internally (see `ResolvedShowOptions.layout`), not a presentation this file
 * paints for itself. It used to open as an ordinary glass surface — anchored
 * from the tap, or a sheet with no gesture — on the reasoning that a viewer
 * painting its own ground would read as a different product pasted in. What
 * that shipped was a frosted, rounded, half-opaque card with the picture laid
 * on it: bars beside a letterboxed photo, and the panel and the page showing
 * straight through a transparent-background one. A picture is not content ON a
 * surface; it is the surface. The thumbnail supplies the source rectangle and
 * the expanded image covers it, shifting only to stay within the viewport.
 *
 * `push({ component })` — a non-image payload — was never a media viewer at
 * all; it is just `show()`, so it forwards there unchanged.
 */
import { componentText } from '@schmancy/mixins'
import { show } from '@schmancy/overlay/overlay.service'
import type { ResolvedShowOptions } from '@schmancy/overlay/overlay.types'
import { ComponentType } from '@schmancy/router/router.types'
import { Subscription } from 'rxjs'
import './lightbox'
import { lightbox as lightboxDirective, type LightboxOptions } from './lightbox.directive'

export type LightboxConfig = {
	// Image mode
	image?: string
	images?: string[]
	index?: number
	/** Accessible name for the viewer. */
	label?: string
	/** Per-image accessible names, indexed with `images`. */
	labels?: string[]
	/** Thumbnail whose bounds the image expands from and covers. */
	sourceElement?: HTMLElement

	// Component-only mode (no image) — forwarded to show()
	component?: ComponentType

	props?: Record<string, unknown>
}

class LightboxService {
	private static instance: LightboxService
	private open$?: Subscription

	public static getInstance(): LightboxService {
		if (!LightboxService.instance) {
			LightboxService.instance = new LightboxService()
		}
		return LightboxService.instance
	}

	public push(config: LightboxConfig): void {
		// One at a time — a second push replaces the first rather than
		// stacking two viewers.
		this.open$?.unsubscribe()

		if (config.component) {
			this.open$ = show(config.component, { props: config.props }).subscribe()
			return
		}

		const images = config.images?.length ? config.images : config.image ? [config.image] : []
		if (images.length === 0) return
		const gallery = images.length > 1

		// The internal shape (`schmancy-boat` sets `anchor` the same way): `layout`
		// is reachable only from inside this package, so no consumer can ask an
		// ordinary overlay for a viewer's full-viewport ground. With no gesture in
		// the ambient window and no source, the picture opens and
		// collapses about its own centre — which is the right answer for a viewer
		// nothing was touched to open.
		const opts: ResolvedShowOptions = {
			// The overlay owns source-bound placement and the frameless material.
			layout: 'viewer',
			trigger: config.sourceElement,
			ariaLabel: config.label ?? config.labels?.[config.index ?? 0] ?? componentText('lightbox.viewer'),
			props: {
				images: gallery ? images : [],
				src: gallery ? '' : images[0],
				initialIndex: config.index ?? 0,
				label: config.label ?? '',
				labels: config.labels ?? [],
			},
		}
		this.open$ = show('schmancy-lightbox', opts).subscribe()
	}

	public dismiss(): void {
		this.open$?.unsubscribe()
		this.open$ = undefined
	}
}

export type LightboxAPI = {
	(options?: LightboxOptions): ReturnType<typeof lightboxDirective>
	push: (config: LightboxConfig) => void
	dismiss: () => void
}

/** Callable directly as the directive, or via `.push()`/`.dismiss()` as the imperative service. */
export const lightbox: LightboxAPI = Object.assign((options?: LightboxOptions) => lightboxDirective(options), {
	push: (config: LightboxConfig) => LightboxService.getInstance().push(config),
	dismiss: () => LightboxService.getInstance().dismiss(),
})
